/**
 * Block 12 PR-B Phase B3 Part C Steps 18-21 — ToggleableRow.
 *
 * Reusable wrapper для секций В/Г/Е (Additional/Filling/Consumables) с общим
 * паттерном «toggle on/off → render ResultRow с qty+price». Принимает id +
 * label + unit + handlers; не имеет formula auto-bind (qty всегда manual).
 *
 * Использование:
 *   <ToggleableRow id="facade-radius" label="Фасад радиусный" unit="шт"
 *     enabled={state.additionalToggles['facade-radius']}
 *     qty={state.additionalQuantities['facade-radius'] ?? 0}
 *     price={state.additionalPrices['facade-radius'] ?? 0}
 *     onToggle={(on) => dispatch...}
 *     onQty={(q) => dispatch...}
 *     onPrice={(p) => dispatch...} />
 */

import { ResultRow } from './ResultRow';
import type { ResultRowData } from '../types';

export interface ToggleableRowProps {
  id: string;
  label: string;
  unit: string;
  enabled: boolean;
  qty: number;
  price: number;
  onToggle: (on: boolean) => void;
  onQty: (qty: number) => void;
  onPrice: (price: number) => void;
  /** Optional deeplink target (Block 2/4/5/8 — ADR-0099). */
  deeplink?: 'decor' | 'partner' | 'furniture' | 'handles';
  onDeeplink?: () => void | Promise<void>;
  /**
   * PR-H1 Round 3 — pass-through brand props в underlying ResultRow.
   * Брэнд input visible когда `onBrandChange` передан caller'ом
   * (SectionAdditional). FacadeRadiusBlock использует ToggleableRow internally
   * и тоже получает pass-through автоматически.
   */
  brand?: string;
  onBrandChange?: (brand: string) => void;
}

export function ToggleableRow({
  id,
  label,
  unit,
  enabled,
  qty,
  price,
  onToggle,
  onQty,
  onPrice,
  deeplink,
  onDeeplink,
  brand,
  onBrandChange,
}: ToggleableRowProps) {
  if (!enabled) {
    return (
      <button
        type="button"
        onClick={() => onToggle(true)}
        data-testid={`toggle-add-${id}`}
        className="min-h-tap rounded-xl border-[1.5px] border-dashed border-accent bg-card text-ink font-semibold text-base px-4 text-left flex items-center justify-between focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
      >
        <span>+ Добавить «{label}»</span>
      </button>
    );
  }

  const cost = qty * price;
  const row: ResultRowData = {
    id,
    name: label,
    qty,
    qtyAuto: 0,
    qtyOverridden: false,
    unit,
    price,
    cost,
    deeplink,
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`toggleable-${id}`}>
      <ResultRow
        row={row}
        onQtyChange={onQty}
        onPriceChange={onPrice}
        onDeeplink={deeplink && onDeeplink ? onDeeplink : undefined}
        brand={brand}
        onBrandChange={onBrandChange}
      />
      <button
        type="button"
        onClick={() => onToggle(false)}
        data-testid={`toggle-remove-${id}`}
        className="min-h-tap rounded-xl border-[1.5px] border-accent bg-card text-muted font-semibold text-sm px-4 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none self-end"
      >
        Убрать
      </button>
    </div>
  );
}
